/**
 * Full Restore Cards — Restoration Candidacy Quiz
 * Assessment engine — pure logic, no DOM/UI code.
 *
 * This file implements the decision tree from the "Restoration Quiz
 * Blueprint" (v3, updated). It exports a single function, assess(answers, config),
 * that takes a plain object of quiz answers and returns a verdict plus
 * the reasoning behind it. Nothing in here touches the page — a UI layer
 * collects answers into the shape below, calls assess(), and renders
 * whatever comes back. That split is deliberate so the visual design of
 * the quiz can be redone later without touching this file.
 *
 * ---------------------------------------------------------------------
 * EXPECTED ANSWERS SHAPE
 * ---------------------------------------------------------------------
 * {
 *   q1_value: 'under100' | '100_300' | '300_500' | '500_1000' |
 *             '1000_2500' | '2500_plus' | 'not_sure',
 *
 *   q2_status: 'raw' | 'graded',
 *   q2a_current_grade: null | '1'..'10' | 'authentic' | 'altered_no_grade' |
 *             'other' | 'not_sure',   // only asked/used when q2_status === 'graded'
 *
 *   q3_goal: 'binder_display' | 'submit_grading' | 'regrade' | 'sell' | 'not_sure',
 *   q3a_sell_plan: null | 'raw_ungraded' | 'grade_before_selling' | 'not_sure', // only when q3_goal === 'sell'
 *
 *   q4_damage_types: array, any of:
 *     'crease' | 'bubble_crease' | 'indent' | 'edge_lift' | 'delamination' |
 *     'warping' | 'contamination' | 'scratching' | 'corner_edge_wear' |
 *     'corner_edge_structural' | 'multiple_unspecified' | 'other',
 *
 *   // Conditional, only present when the matching type above is selected.
 *   // "front"/"both" location values additionally unlock a holo sub-question
 *   // for crease/indent (bubble_crease asks the holo question directly, with
 *   // no front/back split — it's treated as a front-layer defect).
 *   q4a_crease_location: null | 'front' | 'back' | 'both' | 'not_sure',
 *   q4a_crease_holo: null | 'holo' | 'non_holo' | 'both' | 'not_sure',
 *   q4b_indent_location: null | 'front' | 'back' | 'both' | 'not_sure',
 *   q4b_indent_holo: null | 'holo' | 'non_holo' | 'both' | 'not_sure',
 *   q4c_bubble_crease_holo: null | 'holo' | 'non_holo' | 'both' | 'not_sure',
 *
 *   q5_whitening: 'none' | 'light' | 'moderate' | 'heavy' | 'very_heavy' | 'not_sure',
 *   q6_overall_condition: 'very_clean' | 'clean' | 'moderate_wear' | 'heavy_wear' |
 *             'very_heavily_played' | 'not_sure',
 *   q7_main_severity: 'light' | 'moderate' | 'heavy' | 'severe' | 'not_sure',
 *
 *   q8_expectation: 'noticeably_better' | 'reduced_as_much_as_possible' |
 *             'best_possible' | 'specific_grade' | 'not_sure',
 *   q8a_desired_grade: null | 'min_5' | 'min_6' | 'min_7' | 'min_8' | 'min_9' |
 *             'min_10' | 'not_sure',  // only when q8_expectation === 'specific_grade'.
 *             Treated as a MINIMUM acceptable grading outcome, not a predicted
 *             exact grade — this is an expectation-realism check, never a
 *             promise about what grade a card will actually receive.
 * }
 *
 * ---------------------------------------------------------------------
 * RETURN SHAPE
 * ---------------------------------------------------------------------
 * {
 *   verdict: 'strong' | 'potential' | 'probably_not',
 *   reasons: string[],       // human-readable, internal — what drove the verdict
 *   flags: { ...booleans/values used along the way, for debugging/QA }
 * }
 *
 * ---------------------------------------------------------------------
 * GOVERNING PHILOSOPHY (unchanged)
 * ---------------------------------------------------------------------
 * "Improvement, not perfection." A difficult defect does NOT automatically
 * mean Potential or Unlikely. A card can be a Strong candidate even with
 * significant damage if the value/economics make sense, the rest of the
 * card is suitable, and the customer's expectations realistically align
 * with what restoration can achieve. Damage severity informs what level of
 * improvement is realistically achievable — it is the GOAL and EXPECTATION
 * comparison against that achievable improvement that drives the verdict,
 * not severity read off a fixed ladder. "Difficult damage" and "unrealistic
 * expectation" are two different things and the engine keeps them separate.
 *
 * ---------------------------------------------------------------------
 * CONFIG — tune pricing/economics here without touching the tree itself
 * ---------------------------------------------------------------------
 */

const DEFAULT_CONFIG = {
  // Current FRC service pricing (AUD). Update these whenever pricing changes —
  // nothing else in this file needs to change as a result.
  serviceCosts: {
    cardClean: 29.95,
    fullRestoreRaw: 124.95,
    fullRestoreSlab: 149.95,
  },

  // Rough internal value estimate per Q1 band, used ONLY for the economics
  // sanity check below — never shown to the customer, and deliberately
  // conservative (low end of each band). "not_sure" is treated as unknown.
  valueBandEstimate: {
    under100: 50,
    '100_300': 100,
    '300_500': 300,
    '500_1000': 500,
    '1000_2500': 1000,
    '2500_plus': 2500,
    not_sure: null,
  },

  // How many multiples of the service cost the card's estimated value should
  // clear for restoration to "reasonably justify" itself on financial goals.
  // This is an internal tuning knob, not a customer-facing figure — change
  // freely as real submissions give you a better feel for where this should sit.
  economicsJustificationMultiplier: 2,
};

// ---------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------

const SEVERITY_RANK = { light: 1, moderate: 2, heavy: 3, severe: 4, not_sure: 2 };
const WEAR_RANK = {
  none: 0, very_clean: 0,
  light: 1, clean: 1,
  moderate: 2, moderate_wear: 2,
  heavy: 3, heavy_wear: 3,
  very_heavy: 4, very_heavily_played: 4,
  not_sure: 2,
};

// Internal-only ranking for the three possible Gate 1 results, used to combine
// the base expectation check with the crease-specific ceiling (worse wins).
const EXPECTATION_RANK = { pass: 0, uncertain: 1, severe: 2 };

function severityRank(v) { return SEVERITY_RANK[v] ?? 2; }
function wearRank(v) { return WEAR_RANK[v] ?? 2; }
function isNumericGrade(v) { return typeof v === 'string' && /^([1-9]|10)$/.test(v); }
function isMinGrade(v) { return typeof v === 'string' && /^min_(5|6|7|8|9|10)$/.test(v); }
function minGradeNumber(v) { return isMinGrade(v) ? parseInt(v.slice(4), 10) : null; }

// Which service a customer would most likely need, used only for the
// economics sanity check. Adjust this mapping if the business logic for
// which tier applies changes — it's intentionally isolated here.
function relevantServiceCost(answers, config) {
  const onlyContamination =
    answers.q4_damage_types.length === 1 && answers.q4_damage_types[0] === 'contamination';
  if (onlyContamination) return config.serviceCosts.cardClean;
  if (answers.q2_status === 'graded') return config.serviceCosts.fullRestoreSlab;
  return config.serviceCosts.fullRestoreRaw;
}

// ---------------------------------------------------------------------
// Gate 1 — Expectation realism
// ---------------------------------------------------------------------
// Rewritten for MINIMUM-desired-grade semantics (previously an exact desired
// grade). The underlying gap-threshold math against a current/estimated
// condition is unchanged from before — only the meaning of "desired" moved
// from "the grade I expect" to "the grade I'd be happy with or better."
//
// Additively, a crease-specific ceiling is layered on top (never applied to
// any other damage type, and never applied unless the customer explicitly
// asked for a minimum grading outcome): a moderate-or-worse traditional
// crease treats 7+ as the normal realistic questionnaire ceiling. This is an
// internal heuristic only — never stated to the customer as a rule, and
// never applied when the customer's goal isn't a specific grading outcome
// (best-possible-improvement / binder-display / resale-presentation goals
// are never ceiling-capped by crease severity here).
function assessExpectations(answers) {
  if (answers.q8_expectation !== 'specific_grade') {
    return { result: 'pass', reason: 'Stated goal scales naturally with whatever the card can achieve.' };
  }

  const desired = answers.q8a_desired_grade;
  const conditionSeverity = Math.max(
    severityRank(answers.q7_main_severity),
    wearRank(answers.q5_whitening),
    wearRank(answers.q6_overall_condition)
  );
  const heavilyWorn = conditionSeverity >= 3; // heavy or worse on any signal

  let base;
  if (!isMinGrade(desired)) {
    // "Not sure" or unclear minimum — can't be judged precisely.
    base = { result: 'uncertain', reason: 'Minimum desired outcome isn’t clearly stated, can’t be judged against condition alone.' };
  } else {
    const desiredNum = minGradeNumber(desired);

    if (answers.q2_status === 'graded' && isNumericGrade(answers.q2a_current_grade)) {
      // Graded card: current grade is a real, stronger anchor.
      const current = parseInt(answers.q2a_current_grade, 10);
      const gap = desiredNum - current;
      if (gap <= 1) base = { result: 'pass', reason: `Minimum desired grade is close to the card’s current grade (${current} → ${desiredNum}+).` };
      else if (gap <= 3) base = { result: 'uncertain', reason: `Meaningful gap between current (${current}) and minimum desired (${desiredNum}+) grade — needs a physical look.` };
      else base = { result: 'severe', reason: `Large gap between current (${current}) and minimum desired (${desiredNum}+) grade.` };
    } else {
      // Raw card, or graded but current grade unknown/non-numeric: judge
      // minimum desired grade against the condition answers instead.
      if (desiredNum >= 9 && heavilyWorn) {
        base = { result: 'severe', reason: 'A near-perfect minimum outcome is requested but the condition answers describe real, heavier wear or damage.' };
      } else if (desiredNum >= 9 && conditionSeverity <= 1) {
        base = { result: 'pass', reason: 'High minimum desired grade, but the condition answers describe a very clean card — plausible.' };
      } else if (conditionSeverity >= 3) {
        base = { result: 'uncertain', reason: 'Minimum desired grade can’t be confidently judged against a raw card with real wear/damage from the answers alone.' };
      } else {
        base = { result: 'pass', reason: 'Minimum desired grade is broadly in line with the described condition.' };
      }
    }
  }

  // ---- Crease-specific ceiling (internal heuristic only) ----
  // Only ever relevant when: a specific minimum grading outcome was
  // requested, a numeric minimum was actually given, "crease" (traditional
  // crease — not bubble crease) is one of the selected damage types, and the
  // main-defect severity is moderate or worse. It never fires for a
  // non-grading goal, and it never softens the base result — only ever
  // matches or worsens it.
  const desiredNum = minGradeNumber(desired);
  const hasTraditionalCrease = (answers.q4_damage_types || []).includes('crease');
  const creaseSeverity = severityRank(answers.q7_main_severity);

  if (desiredNum != null && hasTraditionalCrease && creaseSeverity >= 2) {
    let ceiling = null;
    if (desiredNum === 10) {
      ceiling = { result: 'severe', reason: 'A moderate-or-worse crease combined with wanting a 10 is beyond what’s realistically achievable.' };
    } else if (desiredNum === 8 || desiredNum === 9) {
      ceiling = { result: 'uncertain', reason: 'A moderate-or-worse crease makes an 8+ or 9+ minimum outcome ambitious — needs a visual look to judge confidently.' };
    }
    // 5, 6, 7: no ceiling — a moderate-or-worse crease and a 7-or-better
    // minimum can still be a straightforward pass.

    if (ceiling && EXPECTATION_RANK[ceiling.result] > EXPECTATION_RANK[base.result]) {
      return ceiling;
    }
  }

  return base;
}

// ---------------------------------------------------------------------
// Gate 2 — Fundamental cleanliness
// ---------------------------------------------------------------------
function assessCleanliness(answers) {
  const w5 = wearRank(answers.q5_whitening);
  const w6 = wearRank(answers.q6_overall_condition);
  const heavyFloor = w5 >= 3 && w6 >= 3;
  const bigMismatch = Math.abs(w5 - w6) >= 3;

  return {
    heavyFloor,
    bigMismatch,
    reason: heavyFloor
      ? 'Whitening and overall condition both describe a heavily worn card — Strong is off the table regardless of goal.'
      : (bigMismatch
        ? 'Whitening and overall condition answers disagree noticeably — logged as a minor uncertainty, not penalised.'
        : 'Card reads as reasonably clean overall.'),
  };
}

// ---------------------------------------------------------------------
// Gate 3 — Is the main problem one we fix?
// ---------------------------------------------------------------------
// Category behaviour per damage type:
//   "favourable"              — never caps, regardless of severity.
//   "expectation_gated"       — a difficult defect on its own does NOT cap
//                                Strong here. Severity is only ever weighed
//                                against the customer's stated grading
//                                expectation, in Gate 1 (crease only has a
//                                ceiling table there; bubble_crease and
//                                indent have none, matching the brief's
//                                "a difficult defect does not automatically
//                                mean Potential or Unlikely").
//   "scaled"                  — legacy numeric-severity cap (kept for any
//                                future defect that should use it; nothing
//                                currently maps here after this update).
//   "scaled_high_ceiling"     — used for edge_lift and delamination: only
//                                the most extreme severity (needing foreign
//                                adhesive / true structural failure) is
//                                treated as outside what restoration fixes.
//   "scaled_conservative"     — indent: unchanged from before.
//   "scaled_unfixable_at_high_severity" — scratching: unchanged.
//   "condition_signal"        — corner/edge whitening: unchanged, folded
//                                into Gate 2 instead of scored here.
//   "conservative"            — structural corner/edge: unchanged, always
//                                caps.
//   "unknown"                 — unclassified: unchanged, always caps.
const DAMAGE_CATEGORY = {
  contamination: 'favourable',
  crease: 'expectation_gated',
  bubble_crease: 'expectation_gated',
  warping: 'scaled',
  edge_lift: 'scaled_high_ceiling',
  delamination: 'scaled_high_ceiling',
  indent: 'scaled_conservative',
  scratching: 'scaled_unfixable_at_high_severity',
  corner_edge_wear: 'condition_signal',
  corner_edge_structural: 'conservative',
  multiple_unspecified: 'unknown',
  other: 'unknown',
};

function assessDamageWorkability(answers, goalFraming) {
  const severity = severityRank(answers.q7_main_severity);
  const types = answers.q4_damage_types || [];

  let capPotential = false;      // blocks Strong, doesn't push to Probably Not
  let unfixablePresent = false;  // heavy scratching / extreme edge lift / extreme delamination
  let unknownPresent = false;
  const reasons = [];

  for (const type of types) {
    const category = DAMAGE_CATEGORY[type] || 'unknown';

    switch (category) {
      case 'favourable':
        // No cap from this alone.
        break;

      case 'expectation_gated':
        // Crease / bubble crease: a difficult defect here does NOT by itself
        // cap Strong. Severity is logged descriptively only — any real
        // constraint on grading-outcome expectations is handled in Gate 1's
        // crease ceiling table, not here, and it never applies to bubble
        // crease at all (no ceiling table exists for it, by design).
        if (severity >= 3) {
          reasons.push(`${type === 'bubble_crease' ? 'Bubble crease' : 'Crease'}: heavy/severe, but a difficult defect alone doesn’t rule out a meaningful improvement — checked against the customer’s stated goal instead.`);
        }
        break;

      case 'scaled':
        if (severity >= 3) { capPotential = true; reasons.push(`${type}: heavy/severe — caps at Potential.`); }
        break;

      case 'scaled_high_ceiling':
        // edge_lift / delamination: only the most extreme severity is
        // treated as the foreign-adhesive / structural-failure case that
        // sits outside what restoration meaningfully fixes.
        if (severity >= 4) {
          unfixablePresent = true;
          reasons.push(type === 'delamination'
            ? 'Delamination at extremely heavy severity — would need foreign adhesive to correct, which isn’t something we present as a strong restoration candidate.'
            : 'edge_lift at severe — treated as possible delamination needing foreign adhesive.');
        } else if (severity === 3) {
          capPotential = true;
          reasons.push(type === 'delamination'
            ? 'Delamination: heavy — results can be more limited, caps at Potential pending a real look.'
            : 'edge_lift: significant lift — caps at Potential pending a real look.');
        } else if (type === 'delamination') {
          reasons.push('Delamination: small/localised — can be a strong candidate on an otherwise clean card.');
        }
        break;

      case 'scaled_conservative':
        // indent: same numeric threshold as before — a moderate indent can
        // still be Strong on an otherwise good card, front or back treated
        // approximately the same regardless of holo involvement.
        if (severity >= 3) { capPotential = true; reasons.push('indent: heavy/deep — caps at Potential (a visual trace can remain even after a good result).'); }
        else if (severity === 2) { reasons.push('indent: moderate — no cap by itself, but can still leave a slight residual trace.'); }
        break;

      case 'scaled_unfixable_at_high_severity':
        if (severity >= 3) {
          unfixablePresent = true;
          reasons.push('scratching: heavy/extensive — not something restoration meaningfully corrects.');
        } else if (severity === 2) {
          capPotential = true;
          reasons.push('scratching: moderate — caps at Potential.');
        }
        break;

      case 'condition_signal':
        // corner/edge whitening: not scored here, it's a Gate 2 signal.
        reasons.push('corner/edge whitening noted as a condition factor, not scored as a fixable defect.');
        break;

      case 'conservative':
        capPotential = true;
        reasons.push('Structural corner/edge damage present — conservative by policy, caps at Potential.');
        break;

      case 'unknown':
        unknownPresent = true;
        reasons.push(`${type}: unclassified — needs photos, caps at Potential.`);
        break;
    }
  }

  // Does the stated goal actually depend on the unfixable issue being resolved?
  const goalDependsOnFix = goalFraming === 'financial_strict' || goalFraming === 'financial_visual';

  return { capPotential, unfixablePresent, unknownPresent, goalDependsOnFix, reasons };
}

// ---------------------------------------------------------------------
// Gate 4 — Multiple / unidentified defects
// ---------------------------------------------------------------------
function assessComplexity(answers) {
  const types = answers.q4_damage_types || [];
  const specificTypes = types.filter((t) => t !== 'multiple_unspecified' && t !== 'other');
  const knownComplexity = specificTypes.length >= 2;
  const unknownComplexity = types.includes('multiple_unspecified') || types.includes('other');

  return {
    knownComplexity,
    unknownComplexity,
    reason: unknownComplexity
      ? 'Unspecified/unknown damage present — genuine uncertainty, caps at Potential.'
      : (knownComplexity
        ? 'Multiple specific defects named — more variables to verify, caps at Potential if it would otherwise be Strong.'
        : 'Single defect type — no complexity penalty.'),
  };
}

// ---------------------------------------------------------------------
// Gate 5 — Economics (goal-dependent, configurable)
// ---------------------------------------------------------------------
function assessEconomics(answers, goalFraming, config) {
  if (goalFraming === 'personal_visual') {
    return {
      hardFail: false,
      note: 'Personal/display goal — economics never forces a negative verdict; cost-vs-value tension is mentioned in copy only, if relevant.',
    };
  }

  const value = config.valueBandEstimate[answers.q1_value];
  if (value == null) {
    return { hardFail: false, note: 'Card value not given — economics treated as neutral/unknown.' };
  }

  const cost = relevantServiceCost(answers, config);
  const justified = value >= cost * config.economicsJustificationMultiplier;

  return {
    hardFail: !justified,
    cost,
    value,
    note: justified
      ? `Estimated value ($${value}) comfortably clears the relevant service cost ($${cost}).`
      : `Estimated value ($${value}) doesn’t clearly justify the relevant service cost ($${cost}) for a financial goal.`,
  };
}

// ---------------------------------------------------------------------
// Gate 6 — Goal framing
// ---------------------------------------------------------------------
function assessGoalFraming(answers) {
  switch (answers.q3_goal) {
    case 'submit_grading':
    case 'regrade':
      return 'financial_strict';
    case 'sell':
      if (answers.q3a_sell_plan === 'grade_before_selling') return 'financial_strict';
      if (answers.q3a_sell_plan === 'raw_ungraded') return 'financial_visual';
      return 'financial_visual'; // sell + not sure: still a financial goal, judged less strictly
    case 'binder_display':
      return 'personal_visual';
    case 'not_sure':
    default:
      return 'financial_strict'; // conservative default: no goal stated, don't assume economics is exempt
  }
}

// ---------------------------------------------------------------------
// Damage detail — structured, customer-facing-safe summary of the new
// conditional answers, computed once here so the UI layer can build
// result-page copy from clean data instead of parsing internal reason
// strings. Purely descriptive — never itself part of the verdict logic.
// ---------------------------------------------------------------------
function buildDamageDetail(answers) {
  const types = answers.q4_damage_types || [];
  const severity = answers.q7_main_severity;

  const crease = types.includes('crease') ? {
    selected: true,
    location: answers.q4a_crease_location || null,
    holoArea: answers.q4a_crease_holo || null,
    severity,
  } : { selected: false };

  const indent = types.includes('indent') ? {
    selected: true,
    location: answers.q4b_indent_location || null,
    holoArea: answers.q4b_indent_holo || null,
    severity,
  } : { selected: false };

  const bubbleCrease = types.includes('bubble_crease') ? {
    selected: true,
    holoArea: answers.q4c_bubble_crease_holo || null,
    severity,
  } : { selected: false };

  const delamination = types.includes('delamination') ? {
    selected: true,
    severity,
  } : { selected: false };

  const frontHoloInvolved =
    (crease.selected && (crease.location === 'front' || crease.location === 'both') && (crease.holoArea === 'holo' || crease.holoArea === 'both')) ||
    (indent.selected && (indent.location === 'front' || indent.location === 'both') && (indent.holoArea === 'holo' || indent.holoArea === 'both')) ||
    (bubbleCrease.selected && (bubbleCrease.holoArea === 'holo' || bubbleCrease.holoArea === 'both'));

  return { crease, indent, bubbleCrease, delamination, frontHoloInvolved };
}

// ---------------------------------------------------------------------
// Main entry point
// ---------------------------------------------------------------------
function assess(answers, userConfig) {
  const config = {
    ...DEFAULT_CONFIG,
    ...userConfig,
    serviceCosts: { ...DEFAULT_CONFIG.serviceCosts, ...(userConfig && userConfig.serviceCosts) },
    valueBandEstimate: { ...DEFAULT_CONFIG.valueBandEstimate, ...(userConfig && userConfig.valueBandEstimate) },
  };

  const reasons = [];
  const goalFraming = assessGoalFraming(answers);

  const gate1 = assessExpectations(answers);
  reasons.push(gate1.reason);

  const gate2 = assessCleanliness(answers);
  reasons.push(gate2.reason);

  const gate3 = assessDamageWorkability(answers, goalFraming);
  reasons.push(...gate3.reasons);

  const gate4 = assessComplexity(answers);
  reasons.push(gate4.reason);

  const gate5 = assessEconomics(answers, goalFraming, config);
  reasons.push(gate5.note);

  const damageDetail = buildDamageDetail(answers);

  // ---- Hard negative gates: any one of these alone is enough ----
  if (gate1.result === 'severe') {
    return finalize('probably_not', reasons, { gate1, gate2, gate3, gate4, gate5, goalFraming, damageDetail },
      'Expectation gap is severe relative to the described condition.');
  }

  if (gate3.unfixablePresent && gate3.goalDependsOnFix) {
    return finalize('probably_not', reasons, { gate1, gate2, gate3, gate4, gate5, goalFraming, damageDetail },
      'Primary issue is outside what restoration meaningfully fixes, and the stated goal depends on it being resolved.');
  }

  if (gate5.hardFail && gate2.heavyFloor) {
    return finalize('probably_not', reasons, { gate1, gate2, gate3, gate4, gate5, goalFraming, damageDetail },
      'Heavy overall wear combined with economics that don’t justify a financial goal.');
  }

  if (gate5.hardFail) {
    // Economics alone, on a financial goal, without a corroborating heavy-wear
    // floor: still a real negative signal, but presented as Probably Not
    // rather than escalated further — matches "can independently push
    // toward Probably Not" from the blueprint.
    return finalize('probably_not', reasons, { gate1, gate2, gate3, gate4, gate5, goalFraming, damageDetail },
      'Economics don’t reasonably justify the cost for a financial goal.');
  }

  // NOTE: deliberately no standalone "multiple severe problems" hard gate here.
  // Q7 only rates the main defect's severity, not every selected defect, so
  // "2+ damage types + severe main defect" is not enough evidence on its own
  // to conclude restoration won't help — it only ever caps at Potential
  // (via gate3.capPotential / gate4.knownComplexity below). A "won't
  // meaningfully help" conclusion can still emerge, but only by way of the
  // hard gates above (e.g. heavy wear + failed economics + financial goal).

  // ---- Potential caps: block Strong, never escalate to Probably Not ----
  const potentialCapReasons = [];
  if (gate1.result === 'uncertain') potentialCapReasons.push('Expectation gap can’t be confidently judged from the answers.');
  if (gate2.heavyFloor) potentialCapReasons.push('Heavy overall wear.');
  if (gate3.capPotential) potentialCapReasons.push('A selected defect caps at Potential given its severity.');
  if (gate3.unfixablePresent && !gate3.goalDependsOnFix) potentialCapReasons.push('An unfixable-category defect is present, but the stated goal doesn’t depend on it — still worth a look.');
  if (gate3.unknownPresent) potentialCapReasons.push('Unclassified damage present.');
  if (gate4.knownComplexity) potentialCapReasons.push('Multiple specific defects named.');
  if (gate4.unknownComplexity) potentialCapReasons.push('Unspecified multiple damage selected.');

  if (potentialCapReasons.length > 0) {
    return finalize('potential', reasons, { gate1, gate2, gate3, gate4, gate5, goalFraming, damageDetail }, potentialCapReasons.join(' '));
  }

  // ---- Nothing capped or failed: Strong ----
  return finalize('strong', reasons, { gate1, gate2, gate3, gate4, gate5, goalFraming, damageDetail },
    'Expectations realistic, card reasonably clean, main damage workable relative to what’s being asked of it, no significant uncertainty, and economics/goal check out.');
}

function finalize(verdict, reasons, flags, summary) {
  return { verdict, summary, reasons: reasons.filter(Boolean), flags };
}

// ---------------------------------------------------------------------
// Exports (works as a plain <script> global too, for the eventual UI)
// ---------------------------------------------------------------------
const RestorationQuizEngine = { assess, DEFAULT_CONFIG };

if (typeof module !== 'undefined' && module.exports) {
  module.exports = RestorationQuizEngine;
}
if (typeof window !== 'undefined') {
  window.RestorationQuizEngine = RestorationQuizEngine;
}
